import { ISelect } from '@tecalisdev/infinity';
import { ISO3166_ALPHA3_TO_ALPHA2 } from '../../core/utils/iso3166-alpha3-to-alpha2';
import { PREFIX_COUNTRIES, PrefixCountryEntry } from './prefix-countries.data';

/** Clave i18n canónica para nombres de país (ISO 3166-1 alpha-3). */
export const PREFIX_COUNTRY_I18N_PREFIX = 'MASTER.COUNTRIES';

export interface PrefixOptionValue {
	iso2: string;
	iso3: string;
	dialCode: string;
	countryName: string;
}

export interface PrefixCountriesOptions {
	/** Traduce ISO3 al nombre localizado. Si falta traducción, devolver el iso3. */
	translateCountry: (iso3: string) => string;
	/** Prefijo de clave i18n; por defecto `MASTER.COUNTRIES`. */
	i18nPrefix?: string;
}

export function prefixCountryTranslationKey(iso3: string, i18nPrefix = PREFIX_COUNTRY_I18N_PREFIX): string {
	return `${i18nPrefix}.${iso3}`;
}

export function extractCountryName(label: string): string {
	return label.replace(/\s*\([^)]+\)\s*$/, '').trim();
}

function resolveIso2(iso3: string): string {
	return ISO3166_ALPHA3_TO_ALPHA2[iso3] ?? '';
}

function buildPrefixLabel(countryName: string, dialCode: string): string {
	return `${countryName} (${dialCode})`;
}

function toPrefixOption(entry: PrefixCountryEntry, countryName: string): ISelect {
	const iso2 = resolveIso2(entry.iso3);
	const id = iso2 || `${entry.dialCode}__${entry.iso3}`;
	return {
		id,
		label: buildPrefixLabel(countryName, entry.dialCode),
		value: {
			iso2,
			iso3: entry.iso3,
			dialCode: entry.dialCode,
			countryName,
		} satisfies PrefixOptionValue,
	};
}

export function getPrefixCountries(options: PrefixCountriesOptions): ISelect[] {
	const i18nPrefix = options.i18nPrefix ?? PREFIX_COUNTRY_I18N_PREFIX;
	return PREFIX_COUNTRIES.map((entry) => {
		const translated = options.translateCountry(entry.iso3);
		const countryName =
			translated && translated !== prefixCountryTranslationKey(entry.iso3, i18nPrefix)
				? translated
				: entry.iso3;
		return toPrefixOption(entry, countryName);
	});
}

export function getPrefixOptionIso2(option: ISelect | undefined | null): string {
	return option?.value?.iso2 ?? '';
}

export function getPrefixOptionIso3(option: ISelect | undefined | null): string {
	return option?.value?.iso3 ?? '';
}

export function getPrefixOptionCountryName(option: ISelect | undefined | null): string {
	return option?.value?.countryName ?? extractCountryName(option?.label ?? '');
}

export function getPrefixOptionDialCode(option: ISelect | undefined | null): string {
	return option?.value?.dialCode ?? '';
}

export function findPrefixOptionById(id: string, options: ISelect[]): ISelect | undefined {
	return options.find((option) => String(option.id) === id);
}

/** Primera coincidencia por prefijo E.164 (p. ej. +1 NANP cuando no hay país explícito). */
export function findPrefixOptionByDialCode(dialCode: string, options: ISelect[]): ISelect | undefined {
	const normalized = dialCode.startsWith('+') ? dialCode : `+${dialCode}`;
	return options.find((option) => option.value?.dialCode === normalized);
}

export function resolvePrefixOption(
	value: string | ISelect | null | undefined,
	options: ISelect[],
): ISelect | undefined {
	if (!value) {
		return undefined;
	}
	if (typeof value === 'object') {
		const iso2 = getPrefixOptionIso2(value);
		const iso3 = getPrefixOptionIso3(value);
		if (iso2 || iso3) {
			return (
				options.find(
					(option) =>
						(iso2 && option.value?.iso2 === iso2) || (iso3 && option.value?.iso3 === iso3),
				) ?? value
			);
		}
		return value;
	}
	if (value.startsWith('+')) {
		return findPrefixOptionByDialCode(value, options);
	}
	return findPrefixOptionById(value, options);
}

export function resolveDialCodeFromPrefixValue(
	value: string | ISelect | null | undefined,
	options: ISelect[],
): string {
	const option = resolvePrefixOption(value, options);
	if (option?.value?.dialCode) {
		return option.value.dialCode;
	}
	if (typeof value === 'string' && value.startsWith('+')) {
		return value;
	}
	return '';
}
